import React from 'react';
import { get, first, isEmpty } from 'lodash';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { DEFAULT_CONTACT_QUERY_PARAMS } from 'constants/sequence';
import PropTypes from 'prop-types';
import { getSequenceContacts } from 'redux/actions/sequenceContactActions';
import { getSequence } from 'redux/actions/sequenceActions';
import {
  getContactPitchesPreview,
  createContactPitchesPreview,
  updateContactPitchesPreview,
} from 'redux/actions/contactActions';
import debounce from 'lodash/debounce';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { orderSteps } from 'utils/steps';
import { destroyStep } from 'redux/actions/stepActions';
import { getCampaign } from 'redux/actions/campaignActions';
import { getFormInitialValues, getFormValues } from 'redux-form';
import closeIcon from 'icons/closeIcon.svg';
import searchIcon from 'icons/searchIcon.svg';
import ContactItem from 'components/modals/StepsPreview/ContactItem';
import Input from 'components/forms/Input';
import Steps from 'components/modals/StepsPreview/Steps';
import Pagination from 'components/pagination/Pagination';
import { Grid } from '@material-ui/core';
import { getCsvTemplateFields } from 'redux/actions/csvTemplateFieldActions';


const styles = () => ({
  contactsSection: {
    maxHeight: '34rem',
    overflow: 'auto',
  },
  fromSection: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '1rem',
    marginLeft: '0.3rem',
    maxHeight: '34rem',
    overflow: 'auto',
  },
  searchContactsInput: {
    position: 'relative',
    marginBottom: '1rem',

    '& > div': {
      height: '2.5rem !important',
    },
    '& input': {
      paddingLeft: '2.5rem !important',
      paddingRight: '2.125rem !important',
    },
  },
  searchIcon: {
    position: 'absolute',
    top: '0.625rem',
    left: '0.75rem',
  },
  closeIcon: {
    cursor: 'pointer',
    position: 'absolute',
    top: '0.75rem',
    right: '0.75rem',
  },
  accountInfo: {
    marginLeft: '0.4rem',
    height: '100%',
    justifyContent: 'space-around',
  },
  accountName: {
    display: 'flex',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '600',
    lineHeight: '1.25rem',
    color: 'black',
  },
  accountEmail: {
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    lineHeight: '1.25rem',
    color: 'gray',
  },
});

class CustomizePitchesTab extends React.Component {
  constructor(props) {
    super(props);
    const firstContact = first(this.props.sequenceContacts.results);
    this.state = {
      steps: [],
      contactSelectedIndex: 0,
      contactSelectedId: firstContact ? firstContact.id : null,
      filterParams: {
        page: 1,
      },
      contactSearchInputValue: '',
    };
    this.searchContactsDebounced = debounce(this.searchContacts, 400);
  }

  componentDidMount() {
    if (!this.props.sequence.loading) {
      this.getContacts(this.state.filterParams);
      this.props.getCampaign(this.props.sequence.campaign);
    }
    this.props.getCsvTemplateFields();
  }

  componentDidUpdate(prevProps) {
    const {
      sequence,
      contactPitchPreviews: { steps },
    } = this.props;
    if (!sequence.loading && !isEqual(prevProps.sequence.loading, sequence.loading)) {
      this.getContacts(this.state.filterParams);
      this.props.getCampaign(this.props.sequence.campaign);
    }
    if (!isEqual(prevProps.contactPitchPreviews.steps, steps)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        steps: orderSteps(steps),
      });
    }
  }

  onContactSearchInputChange = (value) => {
    this.setState({ contactSearchInputValue: value }, () => {
      this.searchContactsDebounced();
    });
  };

  getContacts = async (params) => {
    const {
      sequence: { id: sequenceId },
      contactPitchPreviews: { steps },
    } = this.props;
    await this.props.getSequenceContacts(sequenceId, {
      ...DEFAULT_CONTACT_QUERY_PARAMS,
      page: params.page,
      exlude_self: true,
    });
    this.setState({ filterParams: params || this.state.filterParams });
    const {
      sequenceContacts: { results },
    } = this.props;

    if (results.length > this.state.contactSelectedIndex) {
      this.props.getContactPitchesPreview(results[this.state.contactSelectedIndex].id);
      this.setState({ steps: orderSteps(steps) });
    } else if (first(results)) {
      this.setState(
        {
          contactSelectedIndex: 0,
          contactSelectedId: first(results).id,
        },
        () => {
          this.props.getContactPitchesPreview(first(results).id);
        },
      );
    }
  };

  searchContacts = async () => {
    const { filterParams, contactSearchInputValue } = this.state;
    const {
      sequence: { id: sequenceId },
      contactPitchPreviews: { steps },
    } = this.props;
    await this.props.getSequenceContacts(sequenceId, {
      ...DEFAULT_CONTACT_QUERY_PARAMS,
      page: filterParams.page,
      exlude_self: true,
      search: contactSearchInputValue,
    });

    const {
      sequenceContacts: { results },
    } = this.props;

    if (isEmpty(results)) {
      this.setState({ steps: [] });
    } else {
      this.props.getContactPitchesPreview(this.state.contactSelectedId || results[0].id);
      this.setState({ steps: orderSteps(steps) });
    }
  };

  clearContactSearchInput = () => {
    this.setState({ contactSearchInputValue: '' }, () => {
      this.searchContactsDebounced();
    });
  };

  contactSelected = i => () => {
    const {
      sequenceContacts: { results },
      contactPitchPreviews: { steps },
    } = this.props;
    this.setState(
      {
        contactSelectedIndex: i,
        contactSelectedId: results[i].id,
      },
      () => {
        this.props.getContactPitchesPreview(results[i].id);
      },
    );
    this.setState({ steps: orderSteps(steps) });
  };

  closePitch = (currentStepIndex, currentPitchIndex) => {
    const { steps } = this.state;
    if (steps[currentStepIndex].pitches[currentPitchIndex]) {
      steps[currentStepIndex].pitches[currentPitchIndex] = {
        ...steps[currentStepIndex].pitches[currentPitchIndex],
        collapsed: false,
      };
    } else {
      steps[currentStepIndex].pitches.splice(currentPitchIndex, 1);
    }

    if (isEmpty(steps[currentStepIndex].pitches)) {
      const step = steps.splice(currentStepIndex, 1);
      this.props.destroyStep(step[0].id);
    }
    this.setState({
      steps,
    });
  };

  expandPitch = (stepIndex, pitchIndex) => {
    const { steps } = this.state;

    steps[stepIndex].pitches = steps[stepIndex].pitches.map((pitch, i) => {
      const transformedPitch = pitch;
      transformedPitch.collapsed = i === pitchIndex;
      return transformedPitch;
    });
    this.setState({
      steps,
    });
  };

  render() {
    const {
      sequenceContacts,
      classes,
      formValues,
      initialValues,
      contactPitchPreviews,
      campaign,
    } = this.props;
    const {
      contactSelectedIndex,
      steps,
      contactSearchInputValue,
      filterParams,
    } = this.state;
    const isLoadingSteps = contactPitchPreviews.loading || this.props.sequenceContacts.loading;
    const senderName = get(campaign.email_service_provider_config, 'sender_name');
    return (
      <div>
        <Grid container spacing={2}>
          <Grid item md={3} xs={12}>
            <div className={classes.searchContactsInput}>
              <Input
                placeholder="Search"
                value={contactSearchInputValue}
                onChange={e => this.onContactSearchInputChange(e.target.value)}
              />
              <img
                src={searchIcon}
                alt="search_icon"
                className={classes.searchIcon}
              />
              <img
                src={closeIcon}
                alt="close_icon"
                className={classes.closeIcon}
                role="presentation"
                onClick={this.clearContactSearchInput}
              />
            </div>
            <div className={classes.contactsSection}>
              {sequenceContacts.results.map((c, i) => (
                <ContactItem
                  key={c.id}
                  contact={c}
                  selected={contactSelectedIndex === i}
                  onClick={this.contactSelected(i)}
                  formValues={formValues}
                  initialValues={initialValues}
                  steps={steps}
                />
              ))}
            </div>
            <div className={classes.fromSection}>
              <Avatar classes={{ root: classes.avatarRoot }}>{String(senderName).charAt(0)}</Avatar>
              <div className={classes.accountInfo}>
                <div className={classes.accountName}>
                  <b>{senderName}</b>
                </div>
                <div className={classes.accountEmail}>{campaign.email}</div>
              </div>
            </div>
          </Grid>
          <Grid item md={9} xs={12}>
            {isLoadingSteps ? (
              <div className="loader">
                <LoadingIcon />
              </div>
              ) : (
                <Steps
                  steps={steps}
                  closePitch={this.closePitch}
                  expandPitch={this.expandPitch}
                  formValues={formValues}
                  initialValues={initialValues}
                  contactSelectedId={this.state.contactSelectedId}
                  createContactPitchesPreview={this.props.createContactPitchesPreview}
                  updateContactPitchesPreview={this.props.updateContactPitchesPreview}
                  sequenceContacts={sequenceContacts}
                  contactSelectedIndex={this.state.contactSelectedIndex}
                  selectedContactId={this.state.contactSelectedId}
                  onContactSelected={this.contactSelected}
                />
            )}
          </Grid>
          <Grid item lg={12}>
            <Pagination
              className=""
              items={sequenceContacts}
              onChangePage={this.getContacts}
              params={filterParams}
            />
          </Grid>
        </Grid>
      </div>
    );
  }
}

CustomizePitchesTab.propTypes = {
  classes: PropTypes.element.isRequired,
  createContactPitchesPreview: PropTypes.func.isRequired,
  updateContactPitchesPreview: PropTypes.func.isRequired,
  destroyStep: PropTypes.func.isRequired,
  sequence: PropTypes.shape({
    id: PropTypes.number,
    loading: PropTypes.bool,
    campaign: PropTypes.number,
    account_id: PropTypes.number,
  }).isRequired,
  sequenceContacts: PropTypes.shape({
    loading: PropTypes.bool,
    loaded: PropTypes.bool,
    results: PropTypes.array,
  }).isRequired,
  getSequenceContacts: PropTypes.func.isRequired,
  contactPitchPreviews: PropTypes.shape({
    steps: PropTypes.array,
  }).isRequired,
  formValues: PropTypes.shape({}).isRequired,
  initialValues: PropTypes.shape({}).isRequired,
  getContactPitchesPreview: PropTypes.func.isRequired,
  getCampaign: PropTypes.shape({}).isRequired,
  campaign: PropTypes.shape({
    loading: PropTypes.bool,
  }).isRequired,
  getCsvTemplateFields: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(
    state => ({
      account: state.account,
      sequenceContacts: state.sequenceContacts,
      sequence: state.sequence,
      campaign: state.campaign,
      contactPitchPreviews: state.contactPitchPreviews,
      initialValues: getFormInitialValues('stepsForm')(state),
      formValues: getFormValues('stepsForm')(state),
    }),
    {
      getSequence,
      getSequenceContacts,
      getContactPitchesPreview,
      getCampaign,
      destroyStep,
      createContactPitchesPreview,
      updateContactPitchesPreview,
      getCsvTemplateFields,
    },
  ),
)(CustomizePitchesTab);
